import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { DetailedChangeset } from 'ember-changeset/types';
import { and, not } from 'ember-truth-helpers';
import DateInput from 'garaje/components/date-input';
import removeYour from 'garaje/helpers/remove-your';
import type SignInFieldModel from 'garaje/models/sign-in-field';
import { convertFromISO, convertToISO } from 'garaje/utils/date-format';
import { DateFieldFormat } from 'garaje/utils/enums';

interface EntriesDateFieldSignature {
  Args: {
    changeset: DetailedChangeset<SignInFieldModel>;
    update: (changeset: DetailedChangeset<SignInFieldModel>) => void;
    disabled?: boolean;
    label?: string;
  };
  Element: HTMLDivElement;
}

export default class EntriesDateField extends Component<EntriesDateFieldSignature> {
  @tracked isFocused = false;
  @tracked displayValue: string;

  constructor(owner: unknown, args: EntriesDateFieldSignature['Args']) {
    super(owner, args);
    const existingValue = this.args.changeset.value;
    if (existingValue) {
      this.displayValue = convertFromISO(existingValue, this.dateFormat) ?? existingValue;
    } else {
      this.displayValue = '';
    }
  }

  get dateFormat(): DateFieldFormat {
    return this.args.changeset.dateFormat ?? DateFieldFormat.MM_DD_YYYY;
  }

  get isRequired(): boolean {
    return this.args.changeset.required && !this.args.changeset.allowVisitorRespondents;
  }

  get errorValidation(): string[] {
    return <string[]>this.args.changeset.error?.['value']?.['validation'] || [];
  }

  @action
  handleChange(value: string, isValid: boolean): void {
    this.isFocused = true;
    this.displayValue = value;
    if (isValid) {
      this.args.changeset.value = convertToISO(value, this.dateFormat);
    } else {
      this.args.changeset.value = '';
    }
    this.args.update(this.args.changeset);
  }

  @action
  handleBlur(value: string, isValid: boolean): void {
    this.isFocused = false;
    if (isValid) {
      this.args.changeset.value = convertToISO(value, this.dateFormat);
    }
    this.args.update(this.args.changeset);
  }

  <template>
    <div data-test-flow-field={{@changeset.name}} ...attributes>
      <label>
        {{#if @label}}
          {{@label}}
        {{else}}
          {{removeYour (if @changeset.localized @changeset.localized @changeset.name)}}
        {{/if}}
        {{#if this.isRequired}}
          <span class="requiredField" data-test-required-field></span>
        {{/if}}
        <span class="text-carbon-50 text-xs ml-1" data-test-date-format-hint>({{this.dateFormat}})</span>
      </label>
      <DateInput
        @value={{this.displayValue}}
        @format={{this.dateFormat}}
        @disabled={{@disabled}}
        @required={{this.isRequired}}
        @showError={{if (and this.errorValidation (not this.isFocused)) true}}
        @onChange={{this.handleChange}}
        @onBlur={{this.handleBlur}}
      />
      {{#unless this.isFocused}}
        {{#each this.errorValidation as |errorKey|}}
          <div class="bottom-error pt-1" data-test-error>{{errorKey}}</div>
        {{/each}}
      {{/unless}}
    </div>
  </template>
}
